/**
 * 圖片壓縮工具
 * 用於將圖片壓縮至指定大小並轉換為 Base64
 */

/**
 * 將圖片檔案壓縮並轉換為 Base64
 * @param {File} file - 圖片檔案
 * @param {Object} options - 壓縮選項
 * @param {number} options.maxWidth - 最大寬度（預設 1920px）
 * @param {number} options.quality - JPG 品質 0-1（預設 0.75）
 * @param {boolean} options.convertPngToJpeg - 是否將 PNG 轉為 JPG（預設 true，除非有透明背景）
 * @returns {Promise<{base64: string, originalSize: number, compressedSize: number}>}
 */
export async function compressImage(file, options = {}) {
  const {
    maxWidth = 1920,
    quality = 0.75,
    convertPngToJpeg = true,
  } = options;

  return new Promise((resolve, reject) => {
    // 檢查檔案類型
    if (!file.type.startsWith('image/')) {
      reject(new Error('檔案不是圖片格式'));
      return;
    }

    const originalSize = file.size;
    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();
      
      img.onload = () => {
        // 計算壓縮後的尺寸
        let width = img.width;
        let height = img.height;

        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }

        // 建立 canvas
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');

        // 繪製圖片
        ctx.drawImage(img, 0, 0, width, height);

        // 決定輸出格式
        let outputFormat = file.type;
        let outputQuality = quality;

        // PNG 轉 JPG（除非有透明背景）
        if (convertPngToJpeg && file.type === 'image/png') {
          // 檢查是否有透明像素
          const imageData = ctx.getImageData(0, 0, width, height);
          const data = imageData.data;
          let hasTransparency = false;

          for (let i = 3; i < data.length; i += 4) {
            if (data[i] < 255) {
              hasTransparency = true;
              break;
            }
          }

          // 如果沒有透明，轉為 JPG
          if (!hasTransparency) {
            outputFormat = 'image/jpeg';
          }
        }

        // GIF 轉 JPG
        if (file.type === 'image/gif') {
          outputFormat = 'image/jpeg';
        }

        // 轉換為 Base64
        const base64 = canvas.toDataURL(outputFormat, outputQuality);
        
        // 計算壓縮後大小（Base64 字串長度）
        const compressedSize = Math.round((base64.length - 22) * 3 / 4); // 移除 data:image/...;base64, 前綴

        resolve({
          base64,
          originalSize,
          compressedSize,
          width,
          height,
          format: outputFormat,
        });
      };

      img.onerror = () => {
        reject(new Error('圖片載入失敗'));
      };

      img.src = e.target.result;
    };

    reader.onerror = () => {
      reject(new Error('檔案讀取失敗'));
    };

    reader.readAsDataURL(file);
  });
}

/**
 * 批次壓縮多張圖片
 * @param {FileList|Array<File>} files - 圖片檔案列表
 * @param {Object} options - 壓縮選項
 * @param {number} options.maxFileSize - 單張圖片最大大小（bytes，預設 2MB）
 * @param {Function} options.onProgress - 進度回調 (current, total, result)
 * @returns {Promise<Array<{success: boolean, file: File, result?: Object, error?: string}>>}
 */
export async function compressImages(files, options = {}) {
  const {
    maxFileSize = 2 * 1024 * 1024, // 2MB
    onProgress = null,
  } = options;

  const results = [];
  const fileArray = Array.from(files);

  for (let i = 0; i < fileArray.length; i++) {
    const file = fileArray[i];

    try {
      // 檢查檔案大小
      if (file.size > maxFileSize) {
        results.push({
          success: false,
          file,
          error: `檔案過大（${formatFileSize(file.size)}），超過 ${formatFileSize(maxFileSize)} 限制`,
        });
        
        if (onProgress) {
          onProgress(i + 1, fileArray.length, results[results.length - 1]);
        }
        continue;
      }

      // 檢查檔案類型
      if (!file.type.startsWith('image/')) {
        results.push({
          success: false,
          file,
          error: '不是圖片格式',
        });
        
        if (onProgress) {
          onProgress(i + 1, fileArray.length, results[results.length - 1]);
        }
        continue;
      }

      // 壓縮圖片
      const result = await compressImage(file);
      results.push({
        success: true,
        file,
        result,
      });

      if (onProgress) {
        onProgress(i + 1, fileArray.length, results[results.length - 1]);
      }
    } catch (error) {
      results.push({
        success: false,
        file,
        error: error.message,
      });

      if (onProgress) {
        onProgress(i + 1, fileArray.length, results[results.length - 1]);
      }
    }
  }

  return results;
}

/**
 * 格式化檔案大小
 * @param {number} bytes - 位元組數
 * @returns {string} 格式化後的字串（例如 "1.5 MB"）
 */
export function formatFileSize(bytes) {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}
